import { ImportPeopleInCourseDto, ImportPersonOnCourseDto } from '@/dto/people/import-people.dto'
import BaseService from './base.service'
import type { CreatePeopleDto } from '@/dto/people/create-people.dto'
import type { UpdatePeopleDto } from '@/dto/people/update-people.dto'
import type { DeletePeopleDto } from '@/dto/people/delete-people.dto'
import OrganizationTypeEnum from '@/enums/organization-type.enum'
import {
  collection,
  doc,
  DocumentReference,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  type QueryDocumentSnapshot,
  type SnapshotOptions,
  updateDoc,
  where
} from 'firebase/firestore'
import Level from '@/enums/level.enum'
import { getUserData } from './user.service'
import { auth, db } from '@/config/firebase'
import AclService from '@/services/acl.service'
import PermissionEnum from '@/enums/permission.enum'
import StatusEnum from '@/enums/status.enum'

export default class PeopleService extends BaseService {
  private aclService = new AclService()

  public async listPeople(queryStr: string = '') {
    const conds = []
    if (queryStr) conds.push(where('name', '>=', queryStr))
    conds.push(orderBy('name', 'asc'))
    const ref = collection(this.getOrganizationRef(), 'people')
    const { docs } = await this.paginateFirestore(ref, conds)
    return docs.map((doc: any) => doc.data())
  }

  public async listAdminsFromSql(
    organizationId: string,
    page_size?: number,
    page?: number,
    q?: string,
    sortby_field?: string,
    sortby_direction?: string,
    level?: string
  ) {
    const response = await this.callMonolith(
      `organizations/${organizationId}/admins`,
      {
        page: page,
        page_size: page_size,
        q: q,
        sortby_field: sortby_field,
        sortby_direction: sortby_direction,
        level: level
      },
      'GET'
    )
    return response.data
  }

  public async listSchoolAdminsFromSql(
    organizationId: string,
    schoolsId: any,
    page_size?: number,
    page?: number,
    q?: string,
    sortby_field?: string,
    sortby_direction?: string,
    level?: string
  ) {
    const response = await this.callMonolith(
      `organizations/${organizationId}/schools/${schoolsId}/admins`,
      {
        page: page,
        page_size: page_size,
        q: q,
        sortby_field: sortby_field,
        sortby_direction: sortby_direction,
        level: level
      },
      'GET'
    )
    return response.data
  }

  public async listAdminInSchool(queryStr: string = '') {
    const conds = []
    conds.push(where('level', '>=', 10))
    if (queryStr) conds.push(where('name', '>=', queryStr))
    conds.push(orderBy('name', 'asc'))
    const ref = collection(this.getOrganizationRef(), 'people')
    const { docs } = await this.paginateFirestore(ref, conds)
    return docs.map((doc: any) => doc.data())
  }

  public async getUserMaxLevel() {
    const user = await getUserData()

    if (!user?.id) {
      return 0
    }

    if (!this.organizationId && user.data()?.active_organization?.id) {
      this.organizationId = user.data().active_organization.id
      localStorage.setItem('activeOrganization', user.data().active_organization.id)
    }

    if (user.data()?.is_super_admin && !this.organizationId) {
      return 100
    }

    if (!this.organizationId) {
      return 0
    }

    const q = query(
      this.getCollectionPath('people', true),
      where('user_id', '==', user.id),
      where('organization_id', '==', this.organizationId as string),
      orderBy('level', 'desc'),
      limit(1)
    )

    const querySnapshot = await getDocs(q)
    return querySnapshot.docs?.[0]?.data()?.level
  }

  public async createPeople(data: CreatePeopleDto) {
    return await this.call<CreatePeopleDto>('/createPeople', data)
  }

  public async updatePeople(data: UpdatePeopleDto) {
    return await this.call<UpdatePeopleDto>('/updatePeople', data)
  }

  public async deletePeople(data: DeletePeopleDto) {
    return await this.call('/deletePeople', data)
  }

  public async getPerson(
    person_id: string,
    type: OrganizationTypeEnum,
    school_id?: string,
    course_id?: string
  ) {
    return (await getDoc(this.getPersonRef(person_id, type, school_id, course_id)))?.data()
  }

  public async getPersonSnapshot(
    person_id: string,
    type: OrganizationTypeEnum,
    school_id?: string,
    course_id?: string
  ) {
    return await getDoc(this.getPersonRef(person_id, type, school_id, course_id))
  }

  public async getPeopleFromFirestore(
    path: string,
    lastDoc?: any,
    levels: string[] = [],
    conds?: any,
    applicationId?: string
  ) {
    let ref = this.getCollectionPath(`/organizations/${this.organizationId}/${path}`, false)

    if (applicationId) {
      ref = ref?.withConverter({
        toFirestore: (data) => data,
        fromFirestore: async (
          snapshot: QueryDocumentSnapshot,
          options: SnapshotOptions
        ): Promise<any> => {
          const data = snapshot.data(options)
          const userApplicationSnapshot = (
            await getDocs(
              query(
                collection(db, 'users', data.user_id, 'applications'),
                where('application_id', '==', applicationId)
              )
            )
          )?.docs?.[0]
          return {
            ...data,
            user_application: userApplicationSnapshot?.exists()
              ? userApplicationSnapshot?.data()
              : null
          }
        }
      })
    }

    return await this.paginateFirestore(
      ref,
      [where('level', 'in', levels), ...(conds?.length > 0 ? conds : []), orderBy('name', 'asc')],
      lastDoc
    )
  }

  public async listSchoolsTeachersFromSql(
    schoolId: string,
    page_size?: number,
    page?: number,
    q?: string,
    sortby_field?: string,
    sortby_direction?: string
  ) {
    const response = await this.callMonolith(
      `organizations/${this.organizationId}/schools/${schoolId}/teachers`,
      {
        page_size: page_size,
        page: page,
        q: q,
        sortby_field: sortby_field,
        sortby_direction: sortby_direction
      },
      'GET'
    )

    return response.data
  }

  public async listTeacherCoursesFromSql(
    organizationId: string,
    schoolId: string,
    courseId: string,
    page_size?: number,
    page?: number,
    q?: string,
    sortby_field?: string,
    sortby_direction?: string
  ) {
    const response = await this.callMonolith(
      `organizations/${organizationId}/schools/${schoolId}/courses/${courseId}/teachers`,
      {
        page_size: page_size,
        page: page,
        q: q,
        sortby_field: sortby_field,
        sortby_direction: sortby_direction
      },
      'GET'
    )

    return response.data
  }

  public async listStudentsCoursesFromSql(
    organizationId: string,
    schoolId: string,
    courseId: string,
    page_size?: number,
    page?: number,
    q?: string,
    sortby_field?: string,
    sortby_direction?: string
  ) {
    const response = await this.callMonolith(
      `organizations/${organizationId}/schools/${schoolId}/courses/${courseId}/students`,
      {
        page_size: page_size,
        page: page,
        q: q,
        sortby_field: sortby_field,
        sortby_direction: sortby_direction
      },
      'GET'
    )

    return response.data
  }

  //Schools

  public async getTeacherCoursesFromFirestore(userId: string) {
    const q = query(
      this.getCollectionPath('people', true),
      where('organization_id', '==', this.organizationId),
      where('level', '==', '10'),
      where('user_id', '==', userId)
    )

    const snapshot = await getDocs(q)

    return snapshot.docs.map((doc) => doc?.data()?.course_id).filter((arg) => arg)
  }

  public async updatePersonLogo(
    personId: string,
    logo: string | null,
    type: OrganizationTypeEnum,
    schoolId?: string,
    courseId?: string
  ): Promise<any> {
    let person_ref = null

    if (type === OrganizationTypeEnum.SCHOOL) {
      person_ref = doc(this.getOrganizationRef(), 'schools', schoolId as string, 'people', personId)
    } else if (type === OrganizationTypeEnum.COURSE) {
      person_ref = doc(
        this.getOrganizationRef(),
        'schools',
        schoolId as string,
        'courses',
        courseId as string,
        'people',
        personId
      )
    } else {
      person_ref = doc(this.getOrganizationRef(), 'people', personId)
    }

    if (!person_ref) return

    await updateDoc(person_ref, {
      logo
    })

    return logo
  }

  public async getPersonByUserIdInSchool(userId: string, schoolId: string) {
    const schoolRef = doc(this.getOrganizationRef(), 'schools', schoolId)
    const personId = (
      await getDocs(query(collection(schoolRef, 'people'), where('user_id', '==', userId)))
    ).docs?.[0]?.id

    if (!personId) {
      throw new Error('No found person')
    }

    const personRef = doc(this.getOrganizationRef(), 'schools', schoolId, 'people', personId)
    const snapshot = await getDoc(personRef)
    return snapshot?.data()
  }

  public async getPeopleByUserId(userId: string) {
    const q = query(
      this.getCollectionPath('people', true),
      where('organization_id', '==', this.organizationId),
      where('user_id', '==', userId),
      where('level', '>', Level.STUDENT)
    )

    const snapshot = await getDocs(q)

    return snapshot.docs.map((doc) => ({
      ref: doc.ref,
      data: doc.data()
    }))
  }

  public getOrganizationTypeByLevel(level: number): OrganizationTypeEnum {
    if (level >= Number(Level.STUDENT) && level <= Number(Level.TEACHER)) {
      return OrganizationTypeEnum.COURSE
    } else if (level >= Number(Level.COORDINATOR) && level <= Number(Level.PRINCIPAL)) {
      return OrganizationTypeEnum.SCHOOL
    }
    return OrganizationTypeEnum.ORGANIZATION
  }

  public async importTeachersOnCourse(data: ImportPeopleInCourseDto) {
    return await this.call('/importTeachersOnCourse', data)
  }

  public async importTeacherOnCourses(data: ImportPersonOnCourseDto) {
    return await this.call('/importTeacherOnCourses', data)
  }

  public async getPersonSchoolsId() {
    const permission = await this.aclService.checkAndGetPermissionFromUser('list-school')
    const schoolsIdFromPerson = new Set()

    if (permission?.type === PermissionEnum.ORGANIZATION || permission?.is_super_admin) {
      const snapshot = await getDocs(
        query(
          collection(this.getOrganizationRef(), 'schools'),
          where('status', '==', StatusEnum.ACTIVE)
        )
      )
      const schoolsId = snapshot.docs.map((doc) => doc.id)
      for (const schoolId of schoolsId) {
        schoolsIdFromPerson.add(schoolId)
      }
      return Array.from(schoolsIdFromPerson)
    }

    const q = query(
      this.getCollectionPath('people', true),
      where('organization_id', '==', this.organizationId),
      where('level', '>=', Level.STUDENT),
      where('email', '==', auth.currentUser?.email)
    )
    const snapshot = await getDocs(q)
    const schoolsId = snapshot.docs
      .filter((doc) => doc.get('school_id'))
      .flatMap((doc) => doc.get('school_id'))
    for (const schoolId of schoolsId) {
      schoolsIdFromPerson.add(schoolId)
    }
    return Array.from(schoolsIdFromPerson)
  }

  private getPersonRef(
    person_id: string,
    type: OrganizationTypeEnum,
    school_id?: string,
    course_id?: string
  ) {
    let ref: DocumentReference = doc(this.getOrganizationRef(), 'people', person_id)

    if (type === OrganizationTypeEnum.SCHOOL) {
      ref = doc(this.getOrganizationRef(), 'schools', school_id as string, 'people', person_id)
    } else if (type === OrganizationTypeEnum.COURSE) {
      ref = doc(
        this.getOrganizationRef(),
        'schools',
        school_id as string,
        'courses',
        course_id as string,
        'people',
        person_id
      )
    }

    return ref
  }

  /**
   * Remove um professor de uma turma diretamente no Firestore.
   * @param schoolId ID da escola
   * @param courseId ID do curso
   * @param userId ID do usuário do professor
   */
  public async removeTeacherFromCourse(schoolId: string, courseId: string, userId: string) {
    // Busca o documento da pessoa na subcoleção 'people' do curso
    const peopleRef = collection(
      this.getOrganizationRef(),
      'schools',
      schoolId,
      'courses',
      courseId,
      'people'
    )
    const q = query(peopleRef, where('user_id', '==', userId))
    const snapshot = await getDocs(q)
    if (!snapshot.empty) {
      // Remove todos os documentos encontrados (em geral, só deve haver um)
      const batch = (await import('firebase/firestore')).writeBatch(db)
      snapshot.forEach((docSnap) => {
        batch.delete(docSnap.ref)
      })
      await batch.commit()
      return true
    }
    return false
  }
}
